import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SharePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, downloading, success, error
  const [error, setError] = useState('');

  useEffect(() => {
    handleDownload();
  }, [token]);

  const handleDownload = async () => {
    try {
      setStatus('downloading');
      
      const response = await fetch(`${API}/share/${token}`);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to download document');
      }

      // Create blob and trigger download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `legal_opinion.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setStatus('success');
      
      // Auto-close after 3 seconds
      setTimeout(() => {
        window.close();
      }, 3000);
      
    } catch (error) {
      console.error('Download error:', error);
      setStatus('error');
      setError(error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Preparing Download</h2>
            <p className="text-gray-600">Please wait...</p>
          </>
        )}

        {status === 'downloading' && (
          <>
            <Download className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-bounce" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Downloading Document</h2>
            <p className="text-gray-600">Your legal opinion document is being downloaded...</p>
            <div className="mt-6 bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                📄 If the download doesn't start automatically, check your browser's download folder.
              </p>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Download Complete!</h2>
            <p className="text-gray-600 mb-4">Your document has been downloaded successfully.</p>
            <div className="bg-green-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-green-800">
                ✅ Check your Downloads folder for the legal opinion document.
              </p>
            </div>
            <p className="text-xs text-gray-500">This window will close automatically in 3 seconds...</p>
            <button
              onClick={() => window.close()}
              className="mt-4 px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Close Window
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Download Failed</h2>
            <div className="bg-red-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 font-medium mb-2">Error:</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
            
            <div className="mt-6 space-y-3">
              <div className="bg-yellow-50 rounded-lg p-3 text-left">
                <p className="text-xs font-medium text-yellow-800 mb-1">Common issues:</p>
                <ul className="text-xs text-yellow-700 space-y-1 ml-4 list-disc">
                  <li>Link has expired (valid for 24 hours)</li>
                  <li>Download limit reached (max 10 downloads)</li>
                  <li>Invalid or broken link</li>
                </ul>
              </div>
              
              <button
                onClick={handleDownload}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Try Again
              </button>
              
              <button
                onClick={() => window.close()}
                className="w-full px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Close Window
              </button>
            </div>
          </>
        )}

        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Legal Opinion AI - Secure Document Sharing
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharePage;
